import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useProjectStore } from '../../store/useProjectStore';
import { usePdfRenderer } from '../../hooks/usePdfRenderer';
import { pdfUrl } from '../../api/endpoints';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import type { BoqPosition, BoqPageResult, PageClassification } from '../../types';

interface BoqPage {
  file: string;
  page: number;
  title?: string;
  sectionContext?: string;
  positionCount: number;
  hasError: boolean;
}

type ViewMode = 'page' | 'all';

export function BoqTab() {
  const triage = useProjectStore((s) => s.triage);
  const boqExtraction = useProjectStore((s) => s.boqExtraction);

  const [pageIdx, setPageIdx] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('page');

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { renderPage } = usePdfRenderer();

  const pages = useMemo(() => {
    const extractionPages = boqExtraction?.pages;
    if (extractionPages && extractionPages.length > 0) {
      return extractionPages.map((p: BoqPageResult): BoqPage => ({
        file: p.file,
        page: p.page,
        sectionContext: p.section_context,
        positionCount: p.positions?.length ?? 0,
        hasError: !!p.error,
      }));
    }
    if (!triage?.classifications) return [];
    return triage.classifications
      .filter((c: PageClassification) => c.doc_type === 'boq')
      .map((c: PageClassification): BoqPage => ({
        file: c.file,
        page: c.page,
        title: c.title,
        positionCount: 0,
        hasError: false,
      }));
  }, [boqExtraction, triage]);

  const currentPage = pages[pageIdx];

  const currentPositions = useMemo((): BoqPosition[] => {
    if (!currentPage || !boqExtraction) return [];
    const extractionPages = boqExtraction.pages;
    if (extractionPages) {
      const pageData = extractionPages.find(
        (p: BoqPageResult) => p.file === currentPage.file && p.page === currentPage.page,
      );
      if (pageData) return pageData.positions || [];
    }
    return (boqExtraction.positions || []).filter(
      (p) => p.file === currentPage.file && p.page === currentPage.page,
    );
  }, [boqExtraction, currentPage]);

  const renderCurrentPage = useCallback(async () => {
    if (!currentPage || !canvasRef.current) return;
    const url = pdfUrl(currentPage.file);
    await renderPage(canvasRef.current, url, currentPage.page);
  }, [currentPage, renderPage]);

  useEffect(() => {
    renderCurrentPage();
  }, [renderCurrentPage]);

  const navigate = (delta: number) => {
    const next = pageIdx + delta;
    if (next >= 0 && next < pages.length) setPageIdx(next);
  };

  const totalPositions = boqExtraction?.total_positions ?? boqExtraction?.positions?.length ?? 0;
  const pagesWithData = boqExtraction?.pages?.filter((p) => p.positions?.length > 0).length ?? 0;

  return (
    <PanelGroup direction="horizontal">
      <Panel defaultSize={55} minSize={30}>
        <div className="boq-left">
          <div className="page-nav">
            <button className="nav-btn" disabled={pageIdx <= 0} onClick={() => navigate(-1)}>
              &larr;
            </button>
            <select
              className="page-select"
              value={pageIdx}
              onChange={(e) => setPageIdx(Number(e.target.value))}
            >
              {pages.map((p, i) => (
                <option key={i} value={i}>
                  p.{p.page} {p.sectionContext || p.title || ''} ({p.positionCount} pos.)
                </option>
              ))}
            </select>
            <button
              className="nav-btn"
              disabled={pageIdx >= pages.length - 1}
              onClick={() => navigate(1)}
            >
              &rarr;
            </button>
            <span className="page-info">
              {pages.length > 0 ? `${pageIdx + 1} / ${pages.length}` : 'No BoQ pages'}
            </span>
          </div>
          <div className="pdf-container">
            <canvas ref={canvasRef} />
          </div>
        </div>
      </Panel>
      <PanelResizeHandle className="split-divider" />
      <Panel minSize={25}>
        <div className="boq-right">
          <div className="boq-data-header">
            <div className="boq-data-tabs">
              <button
                className={`boq-data-tab ${viewMode === 'page' ? 'active' : ''}`}
                onClick={() => setViewMode('page')}
              >
                This Page
                {currentPositions.length > 0 && (
                  <span className="tab-count">{currentPositions.length}</span>
                )}
              </button>
              <button
                className={`boq-data-tab ${viewMode === 'all' ? 'active' : ''}`}
                onClick={() => setViewMode('all')}
              >
                All Positions
                {totalPositions > 0 && (
                  <span className="tab-count">{totalPositions}</span>
                )}
              </button>
            </div>
            {boqExtraction && (
              <div className="boq-stats-row">
                <span className="boq-stat">{totalPositions} positions</span>
                <span className="boq-stat">{pagesWithData} / {pages.length} pages</span>
              </div>
            )}
          </div>

          <div className="boq-data-content">
            {viewMode === 'page' && (
              <PagePositions
                positions={currentPositions}
                page={currentPage}
              />
            )}
            {viewMode === 'all' && (
              <AllPositionsTable
                positions={boqExtraction?.positions || []}
                currentPage={currentPage}
                onNavigateToPage={(file, page) => {
                  const idx = pages.findIndex((p) => p.file === file && p.page === page);
                  if (idx >= 0) setPageIdx(idx);
                }}
              />
            )}
          </div>
        </div>
      </Panel>
    </PanelGroup>
  );
}

// ── Per-page positions view ──────────────────────────────────────

function PagePositions({
  positions,
  page,
}: {
  positions: BoqPosition[];
  page?: BoqPage;
}) {
  if (!page) {
    return <div className="feed-placeholder">Select a page to view extracted positions</div>;
  }

  if (page.hasError) {
    return <div className="boq-page-error">Extraction error on this page</div>;
  }

  if (positions.length === 0) {
    return (
      <div className="boq-page-empty">
        <div className="empty-icon">—</div>
        <div>No positions extracted from this page</div>
        {page.sectionContext && (
          <div className="text-dim">Section: {page.sectionContext}</div>
        )}
      </div>
    );
  }

  return (
    <div className="boq-page-positions">
      {page.sectionContext && (
        <div className="boq-section-context">
          Section: {page.sectionContext}
        </div>
      )}
      {positions.map((pos, i) => (
        <PositionCard key={i} position={pos} />
      ))}
    </div>
  );
}

function PositionCard({ position }: { position: BoqPosition }) {
  const desc = position.description || '';
  const descLines = desc.split('\n').filter(Boolean);
  const mainDesc = descLines[0] || '';
  const details = descLines.slice(1).join(' ').trim();

  return (
    <div className="boq-position-card">
      <div className="boq-pos-header">
        <span className="boq-pos-number">{position.position_number || '—'}</span>
        <span className="boq-pos-qty">
          {position.quantity ?? '—'} {position.unit || ''}
        </span>
      </div>
      <div className="boq-pos-desc">{mainDesc}</div>
      {details && <div className="boq-pos-details">{details}</div>}
      <div className="boq-pos-meta">
        {position.section_name && (
          <span className="boq-pos-tag">{position.section_name}</span>
        )}
        {position.category && (
          <span className="boq-pos-tag">{position.category}</span>
        )}
        {position.manufacturer && (
          <span className="boq-pos-tag">{position.manufacturer}</span>
        )}
        {position.article_number && (
          <span className="boq-pos-tag mono">{position.article_number}</span>
        )}
      </div>
    </div>
  );
}

// ── All positions table view ─────────────────────────────────────

function AllPositionsTable({
  positions,
  currentPage,
  onNavigateToPage,
}: {
  positions: BoqPosition[];
  currentPage?: BoqPage;
  onNavigateToPage: (file: string, page: number) => void;
}) {
  if (positions.length === 0) {
    return <div className="feed-placeholder">No BoQ positions extracted yet</div>;
  }

  return (
    <div className="boq-all-positions">
      <table className="data-table">
        <thead>
          <tr>
            <th>Pos.</th>
            <th>Description</th>
            <th>Qty</th>
            <th>Unit</th>
            <th>Section</th>
            <th>Page</th>
          </tr>
        </thead>
        <tbody>
          {positions.map((p, i) => {
            const isCurrentPage = currentPage &&
              p.file === currentPage.file && p.page === currentPage.page;
            return (
              <tr key={i} className={isCurrentPage ? 'row-highlight' : ''}>
                <td className="mono">{p.position_number || ''}</td>
                <td className="boq-table-desc">{(p.description || '').split('\n')[0]}</td>
                <td className="text-center">{p.quantity ?? ''}</td>
                <td>{p.unit || ''}</td>
                <td className="text-dim">{p.section_name || p.section_number || ''}</td>
                <td>
                  {p.page && p.file && (
                    <button
                      className="page-ref-link"
                      onClick={() => onNavigateToPage(p.file!, p.page!)}
                    >
                      p.{p.page}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
